import moment from 'moment-business-days'
import {useEffect, useMemo, useCallback} from 'react'
import {useFormState, useWatch} from 'react-hook-form'

import {useFieldConfigurationContext} from '../../configuration/useConfigurationContext'
import {useOrderProcessFormContext} from '../../form/useOrderProcessFormContext'
import {useMix} from '../mix/useMix'

import {getSelectableDateRange, isDateTimeValid, isDateValid} from './dateTime.utils'

export const useDateTime = () => {
  const {control, setValue, trigger, getFieldState} = useOrderProcessFormContext()
  const dateTimeConfiguration = useFieldConfigurationContext('dateTime')
  const [dateTime] = useWatch({name: ['dateTime'], control})
  const {availableDateRange} = useMix()
  const {defaultValues} = useFormState({control: control})
  const {businessDays, businessHours} = dateTimeConfiguration

  const dateTimeError = getFieldState('dateTime')?.error

  const selectableDateRange = useMemo(() => {
    if (!businessDays || !businessHours) {
      return undefined
    }
    return getSelectableDateRange(availableDateRange, businessHours, businessDays)
  }, [availableDateRange, businessDays, businessHours])

  const setDateTime = useCallback(
    (newValue: moment.Moment[]) => {
      setValue(
        'dateTime',
        newValue.map((date) => date.toISOString()),
        {shouldValidate: true}
      )
      // Use specific field trigger to avoid triggering ALL field validations
      void trigger('dateTime')
    },
    [setValue, trigger]
  )

  useEffect(() => {
    if (dateTime) {
      return
    }
    setDateTime([moment(selectableDateRange?.minDate)])
  }, [dateTime, selectableDateRange?.minDate, setDateTime])

  const minDate = selectableDateRange?.minDate
  const maxDate = selectableDateRange?.maxDate

  return {
    error: dateTimeError,
    dateTime,
    setDateTime,
    isDateValid: (dateTime: moment.Moment) =>
      isDateValid(dateTime, minDate, maxDate, businessDays, businessHours?.timeZone),
    isDateTimeValid: (dateTime: moment.Moment) =>
      isDateTimeValid(dateTime, minDate, maxDate, businessDays, businessHours?.timeZone),
    defaultDateTime: defaultValues?.dateTime,
    minDate,
    maxDate,
    ...dateTimeConfiguration
  }
}
